import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import { useLocalSearchParams } from "expo-router";
import { ActivityIndicator, ScrollView, View, Text } from "react-native";
import { z } from "zod";
import { Control, Controller, FormState, useForm } from "react-hook-form";
import { Textarea } from "@/elements/components/ui/textarea";
import { Button } from "@/elements/components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@/elements/components/utils/convex-api";
import { Id } from "@teachloop/backend/_generated/dataModel";
import { useState } from "react";
import LessonCancelledModal from "@/elements/components/modals/lesson-cancelled-modal";
import goBack from "@/elements/components/utils/go-back";

const LateCancelFormSchema = z.object({
  reason: z.string().min(1),
});
const CancelFormSchema = z.object({
  reason: z.string().optional(),
});

function ParentCancelLessonScreen() {
  const { lessonId } = useLocalSearchParams();
  const { data: isLateCancellation = false, isLoading } = useQuery(
    convexQuery(
      api.parent.cancelSession.queries.checkIsLateCancellation,
      !!lessonId ? { sessionId: lessonId as Id<"sessions"> } : "skip"
    )
  );
  const { control, handleSubmit, formState } = useForm({
    defaultValues: {
      reason: "",
    },
    resolver: zodResolver(
      isLateCancellation ? LateCancelFormSchema : CancelFormSchema
    ),
    mode: "onChange",
  });
  const { mutateAsync: cancelSession, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.parent.cancelSession.mutations.cancelSession
    ),
  });
  const [isLessonCancelledModalVisible, setIsLessonCancelledModalVisible] =
    useState(false);

  const onClose = goBack;

  const onCancel = handleSubmit(async ({ reason }) => {
    await cancelSession({
      sessionId: lessonId as Id<"sessions">,
      reason,
    });
    setIsLessonCancelledModalVisible(true);
  });

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={onClose} />
        <AppBar.Title>Back </AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5 gap-6">
            {isLoading ? (
              <View className="h-[200px] justify-center items-center mx-4 bg-teachLoop-creamBase rounded-xl px-8 py-10">
                <ActivityIndicator />
              </View>
            ) : isLateCancellation ? (
              <LateCancellation
                isPending={isPending}
                control={control}
                formState={formState}
                onCancel={onCancel}
              />
            ) : (
              <RegularCancellation
                isPending={isPending}
                control={control}
                formState={formState}
                onCancel={onCancel}
              />
            )}
          </View>
          <View className="h-8" />
        </ResponsiveContent>
      </ScrollView>

      <LessonCancelledModal
        lateCancellation={isLateCancellation}
        isVisible={isLessonCancelledModalVisible}
        onClose={() => {
          setIsLessonCancelledModalVisible(false);
          onClose(true);
        }}
        onDismiss={() => {
          setIsLessonCancelledModalVisible(false);
          onClose();
        }}
      />
    </View>
  );
}

type CancellationProps = {
  control: Control<z.infer<typeof CancelFormSchema>>;
  formState: FormState<z.infer<typeof CancelFormSchema>>;
  onCancel: () => void;
  isPending: boolean;
};

function RegularCancellation({
  control,
  formState,
  onCancel,
  isPending,
}: CancellationProps) {
  return (
    <View className="py-2">
      <View className="gap-8">
        <Text className="font-zilla-slab-medium font-medium text-[28px] leading-[1.2] text-black">
          Are you sure you want to cancel this lesson?
        </Text>
        <Text className="text-base font-geologica-medium font-normal leading-[1.5] text-teachLoop-textPrimary">
          Please provide a reason for cancelling the lesson (optional)
        </Text>

        <Controller
          control={control}
          name="reason"
          render={({ field, fieldState: { error } }) => (
            <Textarea
              placeholder="Please provide a short reason for your cancellation."
              className="h-[167]"
              size="sm"
              multiline
              numberOfLines={4}
              textAlignVertical="top"
              {...field}
              error={!!error?.message}
            />
          )}
        />

        <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-teachLoop-textPrimary">
          This action cannot be undone. Once cancelled, a notification will be
          sent to the Tutor.
        </Text>
        <Button
          fullWidth
          size="md"
          intent="danger"
          className="bg-red-600"
          onPress={onCancel}
          disabled={!formState.isValid || isPending}
        >
          <Button.Text size="md">Cancel lesson</Button.Text>
        </Button>
      </View>
      <View className="h-3" />
    </View>
  );
}

function LateCancellation({
  control,
  formState,
  onCancel,
  isPending,
}: CancellationProps) {
  return (
    <View className="py-2">
      <View className="gap-6">
        <Text className="font-zilla-slab-medium font-medium text-[28px] leading-[1.2] text-black">
          Late cancellation.
        </Text>
        <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-teachLoop-textPrimary">
          You are about to cancel a session with less than 6 hours notice.
          {"\n"}
          {"\n"}
          Cancelling now may incur a fee. Please provide a brief reason,
          this will be sent to the tutor and Loopedin Learning. 
          Final fee at the discretion of the tutor.
        </Text>

        <Text className="text-base font-geologica-medium font-normal leading-[1.5] text-teachLoop-textPrimary">
          Please provide a reason for cancelling the lesson (required)
        </Text>

        <Controller
          control={control}
          name="reason"
          render={({ field, fieldState: { error } }) => (
            <Textarea
              placeholder="Please provide a short reason for your cancellation."
              className="h-[167]"
              size="sm"
              multiline
              numberOfLines={4}
              textAlignVertical="top"
              {...field}
              error={!!error?.message}
            />
          )}
        />

        <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-teachLoop-textPrimary">
          This action cannot be undone. Once cancelled, a notification will be
          sent to the Tutor.
        </Text>
        <Button
          fullWidth
          size="md"
          intent="danger"
          className="bg-red-600"
          onPress={onCancel}
          disabled={!formState.isValid || isPending}
        >
          <Button.Text size="md">Cancel lesson</Button.Text>
        </Button>
      </View>
      <View className="h-3" />
    </View>
  );
}

export default ParentCancelLessonScreen;
